import { Controller } from "@hotwired/stimulus"

const MOBILE_BREAKPOINT_PX = 768
const MODAL_GAP_PX = 12
const VIEWPORT_PADDING_PX = 16

export default class extends Controller {
  static targets = [ "initialFocus", "dialog" ]
  static values = { anchorDate: String }

  connect() {
    this.previousActiveElement = document.activeElement
    this.boundPositionDialog = this.positionDialog.bind(this)
    window.addEventListener("resize", this.boundPositionDialog)
    requestAnimationFrame(() => this.positionDialog())
    this.focusInitialTarget()
  }

  disconnect() {
    window.removeEventListener("resize", this.boundPositionDialog)
  }

  close() {
    this.element.remove()
    this.restoreFocus()
  }

  closeOnBackdrop(event) {
    if (event.target === this.element) this.close()
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  focusInitialTarget() {
    if (this.hasInitialFocusTarget) {
      this.initialFocusTarget.focus()
      return
    }

    const closeButton = this.element.querySelector("[data-action~='meal-log-modal#close']")
    if (closeButton) closeButton.focus()
  }

  restoreFocus() {
    if (this.previousActiveElement && document.contains(this.previousActiveElement)) {
      this.previousActiveElement.focus()
    }
  }

  positionDialog() {
    if (!this.hasDialogTarget) return

    const dialog = this.dialogTarget
    dialog.style.position = "absolute"
    dialog.style.maxHeight = `calc(100vh - ${VIEWPORT_PADDING_PX * 2}px)`
    dialog.style.overflowY = "auto"

    const anchorCell = this.anchorCell()
    if (this.mobileViewport() || !anchorCell) {
      this.centerDialog(dialog)
      return
    }

    const anchorRect = anchorCell.getBoundingClientRect()
    const dialogRect = dialog.getBoundingClientRect()
    const maxLeft = window.innerWidth - dialogRect.width - VIEWPORT_PADDING_PX
    let left = anchorRect.right + MODAL_GAP_PX
    if (left > maxLeft) {
      left = anchorRect.left - dialogRect.width - MODAL_GAP_PX
    }
    if (left < VIEWPORT_PADDING_PX) {
      left = Math.min(Math.max(anchorRect.left, VIEWPORT_PADDING_PX), maxLeft)
    }

    const maxTop = window.innerHeight - dialogRect.height - VIEWPORT_PADDING_PX
    let top = anchorRect.top
    if (maxTop <= VIEWPORT_PADDING_PX) {
      top = VIEWPORT_PADDING_PX
    } else {
      top = Math.min(Math.max(top, VIEWPORT_PADDING_PX), maxTop)
    }

    dialog.style.left = `${Math.round(left)}px`
    dialog.style.top = `${Math.round(top)}px`
  }

  centerDialog(dialog) {
    const dialogRect = dialog.getBoundingClientRect()
    const left = Math.max(VIEWPORT_PADDING_PX, (window.innerWidth - dialogRect.width) / 2)
    const top = Math.max(VIEWPORT_PADDING_PX, (window.innerHeight - dialogRect.height) / 2)

    dialog.style.left = `${Math.round(left)}px`
    dialog.style.top = `${Math.round(top)}px`
  }

  mobileViewport() {
    return window.innerWidth < MOBILE_BREAKPOINT_PX
  }

  anchorCell() {
    const date = this.anchorDate()
    if (!date) return null

    return document.querySelector(`article[data-calendar-date='${date}']`)
  }

  anchorDate() {
    if (this.hasAnchorDateValue && this.anchorDateValue.length > 0) {
      return this.anchorDateValue
    }

    return this.previousActiveElement?.dataset?.quickCreateDate || null
  }
}
